
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

export default function Testimonials() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Client Testimonials",
    "description": "Read what our clients say about NeuraSites AI's web development services",
    "url": `${siteUrl}/testimonials`,
    "mainEntity": {
      "@type": "Organization",
      "name": "NeuraSites AI",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  };


  const testimonials = [
    {
      id: 1,
      name: "Sarah Chen",
      position: "CEO",
      company: "TechFlow Solutions",
      image: "/site-images/sarah-johnson.webp",
      rating: 5,
      text: "NeuraSites AI transformed our online presence completely. The AI-powered website they built increased our lead generation by 340% in just 3 months. The team's expertise in both technology and business strategy is unmatched.",
      project: "SaaS Platform Redesign",
      results: "340% increase in leads, 65% faster load times"
    },
    {
      id: 2,
      name: "Marcus Rodriguez",
      position: "Founder",
      company: "GreenEarth Organics",
      image: "/site-images/michael-chen.webp",
      rating: 5,
      text: "Working with NeuraSites was a game-changer for our e-commerce business. Their AI-driven approach to user experience design resulted in a 280% increase in online sales. The website practically sells itself now!",
      project: "E-commerce Store Development",
      results: "280% sales increase, 45% higher conversion rate"
    },
    {
      id: 3,
      name: "Dr. Emily Watson",
      position: "Medical Director",
      company: "HealthFirst Clinic",
      image: "/site-images/emily-rodriguez.webp",
      rating: 5,
      text: "The patient portal and appointment system they built has revolutionized our practice. Patient satisfaction scores increased by 92%, and our administrative workload decreased significantly. Exceptional work!",
      project: "Healthcare Platform",
      results: "92% patient satisfaction increase, 50% admin reduction"
    },
    {
      id: 4,
      name: "James Thompson",
      position: "Marketing Director",
      company: "Apex Financial",
      image: "/site-images/saas-development-hero.webp",
      rating: 5,
      text: "NeuraSites delivered beyond our expectations. The AI-powered analytics dashboard they integrated gives us insights we never had before. Our marketing ROI improved by 220% within the first quarter.",
      project: "Corporate Website & Analytics",
      results: "220% ROI improvement, real-time insights"
    },
    {
      id: 5,
      name: "Lisa Park",
      position: "Owner",
      company: "Artisan Bakery Co.",
      image: "/site-images/e-commerce-hero.webp",
      rating: 5,
      text: "As a small business owner, I was amazed by how affordable and effective their solution was. The online ordering system increased our revenue by 180%, and the website beautifully showcases our artisan products.",
      project: "Small Business Website",
      results: "180% revenue increase, streamlined operations"
    },
    {
      id: 6,
      name: "Robert Kim",
      position: "CTO",
      company: "DataSync Technologies",
      image: "/site-images/website-development-hero.webp",
      rating: 5,
      text: "The technical expertise and AI integration capabilities of NeuraSites are outstanding. They built a complex data visualization platform that our clients love. Performance improved by 300% compared to our old system.",
      project: "Data Platform Development",
      results: "300% performance improvement, enhanced UX"
    }
  ];

  const stats = [
    { number: "98%", label: "Client Satisfaction Rate" },
    { number: "250%", label: "Average ROI Increase" },
    { number: "48hrs", label: "Average Response Time" },
    { number: "150+", label: "Successful Projects" }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="Client Testimonials - What Our Customers Say | NeuraSites AI"
        description="Read testimonials from satisfied clients who chose NeuraSites AI for their web development needs. See why businesses trust us with their online presence."
        keywords="client testimonials, web development reviews, customer feedback, NeuraSites AI reviews, website development testimonials"
        ogTitle="Client Testimonials - Real Feedback from Real Businesses"
        ogDescription="Read what our clients say about NeuraSites AI's web development services and see why businesses trust us with their online presence."
        canonicalUrl={`${siteUrl}/testimonials`}
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="">
        {/* Hero Section */}
        <section className="relative overflow-hidden brand-soft-surface py-20">
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/assets/pricing_hero-CCxYm27z.jpg')` }}
            />
            <div className="absolute inset-0 brand-soft-overlay" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              What Our Clients
              <span className="block brand-heading-accent">
                Say About Us
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto">
              Real stories from businesses that transformed their online presence with AI-powered websites.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="text-gray-600 dark:text-white font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Testimonial */}
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="brand-cta-surface rounded-2xl p-8 md:p-12 text-center">
              <div className="flex justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
                ))}
              </div>
              <blockquote className="text-xl md:text-2xl font-medium mb-8 leading-relaxed">
                "NeuraSites AI didn't just build us a website – they built us a growth engine. The AI-powered features have automated so much of our customer acquisition process that we've been able to focus on what we do best: serving our clients."
              </blockquote>
              <div className="flex items-center justify-center space-x-4">
                <img 
                  src="/site-images/homepage-network.webp"
                  alt="Featured Client"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="text-left">
                  <div className="font-semibold text-lg">Alexandra Mitchell</div>
                  <div className="brand-cta-copy">CEO, Innovation Labs</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client Testimonials Grid */}
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Client Success Stories</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Discover how we've helped businesses achieve remarkable results
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover mr-4"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{testimonial.name}</h3>
                      <p className="text-gray-600 text-sm">{testimonial.position}</p>
                      <p className="text-blue-600 text-sm font-medium">{testimonial.company}</p>
                    </div>
                  </div>

                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400"></i>
                    ))}
                  </div>

                  <blockquote className="text-gray-700 mb-4 leading-relaxed">
                    "{testimonial.text}"
                  </blockquote>

                  <div className="border-t pt-4">
                    <div className="text-sm text-gray-600 mb-2">
                      <strong>Project:</strong> {testimonial.project}
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      <strong>Results:</strong> {testimonial.results}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 brand-cta-surface">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Join Our Success Stories?
            </h2>
            <p className="text-xl brand-cta-copy mb-8">
              Let's discuss how we can transform your business with an AI-powered website
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/vibe-check"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Start Your Project
                <i className="ri-rocket-fill ml-2"></i>
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#0F2A4E] transition-colors whitespace-nowrap"
              >
                Get Free Consultation
                <i className="ri-phone-fill ml-2"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />

    </div>
  );
}
